import { useMemo } from 'react';
import {
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { styles } from '../monitoringCustomStyles';
import { GranularityEnum, GranularityEnumConfig } from '../constants';
import { getFilteredGranularityOptions } from '../helpers/granularityUtils';

export function SelectGranularity(props) {
  const {
    granularity: selectedGranularity,
    setGranularity,
    startTime,
    endTime,
  } = props;

  // Filter granularity options based on time range
  const filteredOptions = useMemo(() => {
    if (!startTime || !endTime) {
      return Object.values(GranularityEnum).map((id) => {
        const config = GranularityEnumConfig[id];
        return {
          id,
          displayLabel: config.displayLabel,
          value: config.value,
        };
      });
    }
    return getFilteredGranularityOptions(startTime, endTime);
  }, [startTime, endTime]);

  // Use selected granularity if it's in filtered options, otherwise use the largest available
  // Note: Auto-adjustment is handled in useMonitoring hook, this is just for display
  const displayGranularity = useMemo(() => {
    const isValid = filteredOptions.some(
      (option) => option.id === selectedGranularity,
    );
    if (isValid) {
      return selectedGranularity;
    }
    // If current selection is not valid, return the largest available option as fallback
    if (filteredOptions.length > 0) {
      const largestOption = filteredOptions.reduce((prev, current) =>
        current.value > prev.value ? current : prev,
      );
      return largestOption.id;
    }
    return selectedGranularity;
  }, [selectedGranularity, filteredOptions]);

  return (
    <Stack width={'170px'} sx={{ marginRight: 2 }}>
      <FormControl fullWidth>
        <Select
          value={displayGranularity}
          onChange={(e) => {
            setGranularity(e.target.value as GranularityEnum);
          }}
          sx={{
            padding: '0px 0px 0px 0px !important',
            background: styles.color.greyBackground4,
            '.MuiSelect-select': {
              padding: '8px 32px 8px 8px !important',
            },
          }}
          renderValue={(selectedValue) => {
            const config =
              GranularityEnumConfig[selectedValue as GranularityEnum] ||
              GranularityEnumConfig[displayGranularity as GranularityEnum];
            return (
              <Stack
                sx={{
                  flexDirection: 'row',
                  overflow: 'visible',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    display: 'flex',
                    marginRight: '4px',
                    color: styles.color.monotoneDarker,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Granularity:
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    display: 'flex',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {config?.displayLabel || selectedValue}
                </Typography>
              </Stack>
            );
          }}
        >
          {filteredOptions.map((option) => {
            return (
              <MenuItem key={option.id} value={option.id}>
                <Typography>{option.displayLabel}</Typography>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Stack>
  );
}
